import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FeedIcon from '@mui/icons-material/Feed';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { CardContent, CircularProgress, Collapse, Rating, Stack, ToggleButton, Typography } from "@mui/material";
import { ShTextField } from "Components/AppLogin/AppLogin.styled";
import { ResourceElementsBorderRadius, ResourceHomeStack } from 'Components/Resources/Resources.styled';
import {
    AI_TOOLS_DETAILS, AI_TOOLS_TITLE, CUSTOMER_TESTIMONIAL, EXPLORE_SMOOTH_HIRING,
    EXPLORE_SMOOTH_HIRING_DETAILS, HIRE_BEST_CANDIDATES, HIRE_BEST_CANDIDATES_DETAILS
} from 'Components/Resources/ResourcesConstants';
import { IsSmScreen, IsXsScreen, useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ShButton, ShToggleButtonGroup } from 'shared/SharedStyles/ShInputs';
import { ShContainer } from "shared/SharedStyles/ShLayouts";
import { ShPaper } from 'shared/SharedStyles/ShSurfaces';
import { MUIGrey, PrimaryThemeColor } from 'shared/SharedStyles/styleConstants';
import { getAiInterviewQuestions, getAiJobDescriptionByTitle, resetResourcesApiState } from 'store/slices/app/resources-slice';

export const ResourcesHome = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isXsScreen = IsXsScreen();
    const isSmScreen = IsSmScreen();
    const [resource, setResource] = useState<number>(0);
    const [industry, setIndustry] = useState<string>('');
    const [jobCompany, setJobCompany] = useState<string>('');
    const [role, setRole] = useState<string>('');
    const { getAiJobDescStatus, getAiInterviewQuestionsStatus } = useAppSelector(state => state.app.resources);

    const [aiViewTextGenerationAvailable, setAiViewTextGenerationAvailable] = useState<boolean>(false);

    const handleSubmit = () => {
        if (role) {
            const fullRole = `company "${jobCompany}" for the role ${role}`;
            if (resource === 0) {
                dispatch(getAiJobDescriptionByTitle({ role: fullRole, industry: industry }));

            } else if (resource === 1) {
                dispatch(getAiInterviewQuestions({ role: role, industry: industry }));

            }
        }
    };

    useEffect(() => {
        return () => {
            dispatch(resetResourcesApiState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (resource === 0 && getAiJobDescStatus === 'success') {
            navigate("description", { state: { title: role } });
            setAiViewTextGenerationAvailable(true);
        } else if (resource === 1 && getAiInterviewQuestionsStatus === 'success') {
            navigate("interview-kit", { state: { title: role } });
            setAiViewTextGenerationAvailable(true);
        }
    }, [dispatch, getAiJobDescStatus, getAiInterviewQuestionsStatus, navigate, role, resource]);

    return (
        <ShContainer maxWidth='lg' height="100%" margin='auto'>
            <Stack paddingY={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }} height='100%'>
                <Typography textAlign='center' variant='h4' gutterBottom fontWeight={700}
                    color={PrimaryThemeColor}>{AI_TOOLS_TITLE}</Typography>
                <Typography textAlign='center' variant='subtitle2' marginBottom={{ xs: 4, sm: 4, md: 5, lg: 6 }} color={MUIGrey}>
                    {AI_TOOLS_DETAILS}
                </Typography>
                <Stack justifyContent="center" alignItems="center" marginBottom={4}>
                    <ShToggleButtonGroup
                        size='large'
                        borderRadius={ResourceElementsBorderRadius}
                        defaultValue={0}
                        color="info"
                        value={resource}
                        exclusive
                        onChange={(e, nV) => setResource(nV)}
                        aria-label="Resource"
                        orientation={isXsScreen ? 'vertical' : 'horizontal'}
                    >
                        <ToggleButton value={0}>
                            Job Description
                        </ToggleButton>
                        <ToggleButton value={1}>
                            Interview Kit
                        </ToggleButton>
                    </ShToggleButtonGroup>
                </Stack>

                <ResourceHomeStack spacing={2} direction={isXsScreen ? 'column' : 'row'} alignItems="center" justifyContent="center">

                    <ShTextField size='medium' label='Company (Optional)' variant='outlined' fullWidth onChange={(e) => {
                        setJobCompany(e.target.value);
                    }}
                    />

                    <ShTextField size='medium' label='Industry' variant='outlined' fullWidth
                        onChange={(e) => {
                            setIndustry(e.target.value);
                        }}
                    />
                    <ShTextField size='medium' label='Job Role' variant='outlined' fullWidth
                        onChange={(e) => {
                            setRole(e.target.value);
                        }}
                    />
                    <ShButton size='large' disableElevation color="info"
                        disabled={getAiJobDescStatus === 'pending' || getAiInterviewQuestionsStatus === 'pending'}
                        borderRadius={ResourceElementsBorderRadius} startIcon={<AutoAwesomeIcon />} variant='contained'
                        onClick={() => handleSubmit()}><Typography>Generate&nbsp;</Typography>
                        {getAiJobDescStatus === 'pending' || getAiInterviewQuestionsStatus === 'pending' ?
                            <CircularProgress size='1.5rem' /> : ''}</ShButton>

                </ResourceHomeStack>

                <Collapse in={aiViewTextGenerationAvailable} timeout={3000} unmountOnExit>
                    <Outlet />
                </Collapse>

                <Stack direction={isSmScreen ? 'column' : 'row'} paddingTop={6} spacing={2} justifyContent="center" alignItems="center">
                    <ShPaper variant="outlined" borderRadius={20}>
                        <CardContent>
                            <Stack marginBottom={2} paddingLeft={2} >
                                <FeedIcon color='info' fontSize='medium' />
                            </Stack>
                            <Typography variant="h6" marginBottom={2} paddingLeft={2}>{EXPLORE_SMOOTH_HIRING}</Typography>
                            <Typography variant="body1" paddingLeft={2}>{EXPLORE_SMOOTH_HIRING_DETAILS}</Typography>
                            <Stack direction={isSmScreen ? 'column' : 'row'} paddingLeft={2} paddingTop={3} spacing={1}>
                                <ShButton borderRadius={20} variant="outlined" color="success" size='large'
                                    href='https://app.smoothhiring.com/employers/new'> Post a Job </ShButton>
                                <ShButton borderRadius={20} variant="outlined" color="primary" size='large'
                                    href='https://calendly.com/admin-1ue9/30min?month=2024-05'> Schedule A Free Demo {'>'} </ShButton>
                            </Stack>
                        </CardContent>
                    </ShPaper>

                    <ShPaper variant="outlined" borderRadius={20} style={{ marginTop: isSmScreen ? '20px' : 0 }}>

                        <CardContent>
                            <Stack marginBottom={2} paddingLeft={2} >
                                <PersonSearchIcon color='info' fontSize='medium' />
                            </Stack>
                            <Typography variant="h6" paddingLeft={2} marginBottom={2}>{HIRE_BEST_CANDIDATES}</Typography>
                            <Typography variant="body1" paddingLeft={2}>{HIRE_BEST_CANDIDATES_DETAILS}</Typography>
                            <Stack direction={'row'} paddingLeft={2} paddingTop={3}>
                                <ShButton color="primary"> Learn More <KeyboardArrowRightOutlinedIcon /></ShButton>
                            </Stack>
                        </CardContent>
                    </ShPaper>
                </Stack>

                <Stack paddingTop={4} spacing={2} justifyContent="center" alignItems="center">
                    <Typography variant='body2' textAlign='center' maxWidth={600}>{CUSTOMER_TESTIMONIAL.testimonial}</Typography>
                </Stack>
                <Stack padding={2} direction="row" justifyContent="center" spacing={2} alignItems="center">
                    <Typography variant='body2' >{CUSTOMER_TESTIMONIAL.name}</Typography>
                    <Rating size="small" name="fixed-rating" value={CUSTOMER_TESTIMONIAL.rating} readOnly />
                </Stack>

            </Stack>
        </ShContainer>
    )
}